/* eslint-disable @typescript-eslint/no-use-before-define, no-continue -- runtime of the approved UI texts brought from the staging as is: mutually recursive helpers and early-continue loops (docs/home-variantes.md) */
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import type { ApprovedPageData } from '@/utils/approved-content/types';

import { resourcesUiSourceKeys } from './resources-ui-source-map';
import { redactSourceText } from './source-redactions';

export const ApprovedPageContext = createContext<ApprovedPageData | null>(null);

export function ApprovedPageProvider({ data, children }: { data: ApprovedPageData; children: ReactNode }) {
  return <ApprovedPageContext.Provider value={data}>{children}</ApprovedPageContext.Provider>;
}

export const useApprovedPage = () => useContext(ApprovedPageContext);

export interface ApprovedUiValue {
  labels: Readonly<Record<string, string>>;
  sourceTemplates?: readonly ApprovedUiSourceTemplate[];
  /** Suplemento aprovado do idioma do par (src/utils/approved-content/ui-supplement.ts): só para textos-fonte que o bundle do par não traduziu (source-only-copy.tsx). */
  supplement?: Readonly<Record<string, string>>;
}

export const ApprovedUiContext = createContext<ApprovedUiValue | null>(null);

/** Missing translated interface copy is an integration error, never an implicit Portuguese fallback. */
export function approvedUiText(context: ApprovedUiValue | null, key: string, original: string, args: readonly (string | number)[] = []): string {
  // Pages without approved UI show the component's source text, minus any disclaimer (source-redactions.generated.json).
  if (!context) return redactSourceText(original);
  const value = context.labels[key];
  // Owner decision 2026-10-07: a label that was entirely a disclaimer is exported as '' (deliberately empty), so it renders nothing.
  if (value === '') return '';
  if (typeof value !== 'string' || !value.trim()) throw new Error(`Missing approved interface label: ${key}`);
  return value.replace(/\{arg(\d+)\}/g, (_match, index: string) => {
    const argument = args[Number(index)];
    if (argument === undefined) throw new Error(`Missing approved label argument: ${key}/${index}`);
    return String(argument);
  });
}

export function ApprovedUiProvider({ labels, supplement, children }: { labels: { key: string; text: string }[]; supplement?: Readonly<Record<string, string>>; children: ReactNode }) {
  const value = useMemo(() => ({ labels: Object.fromEntries(labels.map((label) => [label.key, label.text])), ...(supplement ? { supplement } : {}) }), [labels, supplement]);
  return <ApprovedUiContext.Provider value={value}>{children}</ApprovedUiContext.Provider>;
}

export const useApprovedUi = () => useContext(ApprovedUiContext);

export function approvedPageUi(data: ApprovedPageData, key: string): string {
  return approvedUiText({ labels: Object.fromEntries(data.page.ui_labels.map((label) => [label.key, label.text])) }, key, '');
}

export interface ApprovedUiSourceTemplate {
  original: string;
  key: string;
  text: string;
  module?: string;
}

/** Evaluate original template arguments once, preserving JS coercion and source whitespace. */
export function approvedUiTemplateEntry(context: ApprovedUiValue | null, key: string, quasis: readonly string[], args: readonly unknown[], module?: string): ApprovedUiSourceTemplate {
  const renderedArgs = args.map((argument) => `${isApprovedUiArgument(argument) ? argument.original : argument}`);
  const original = quasis.reduce((text, quasi, index) => text + quasi + (index < renderedArgs.length ? renderedArgs[index] : ''), '');
  const translatedArgs = args.map((argument, index) => (isApprovedUiArgument(argument) ? argument.text : (renderedArgs[index] as string)));
  const prefix = quasis[0]?.match(/^\s*/)?.[0] ?? '';
  const suffix = quasis[quasis.length - 1]?.match(/\s*$/)?.[0] ?? '';
  const text = context ? prefix + approvedUiText(context, key, original, translatedArgs) + suffix : original;
  return { original, key, text, module };
}

export function approvedUiTemplate(context: ApprovedUiValue | null, key: string, quasis: readonly string[], args: readonly unknown[]): string {
  return approvedUiTemplateEntry(context, key, quasis, args).text;
}

/** Module data stays immutable; only the render context receives resolved template values. */
export function useApprovedUiSourceTemplates(context: ApprovedUiValue | null, factory: (context: ApprovedUiValue) => readonly ApprovedUiSourceTemplate[]): ApprovedUiValue | null {
  return useMemo(() => (context ? { ...context, sourceTemplates: [...factory(context), ...(context.sourceTemplates ?? [])] } : null), [context, factory]);
}

const owns = (object: object, key: string) => Object.prototype.hasOwnProperty.call(object, key);

/** Resolve an exact source value at a public sink, never ids, state, lookups, CSS or DOM trees. */
export function approvedUiSourceValue<T>(context: ApprovedUiValue | null, module: string, value: T): T {
  if (!context) return value;
  if (typeof value === 'string') {
    const templates = context.sourceTemplates ?? [];
    const template = templates.find((entry) => entry.original === value && entry.module === module);
    if (template) return template.text as T;
    if (!owns(resourcesUiSourceKeys, value)) return value;
    const candidates = resourcesUiSourceKeys[value] ?? [];
    const candidate = candidates.find(([owner]) => owner === module);
    if (!candidate) return value;
    const [, key, prefix = '', suffix = ''] = candidate;
    return (prefix + approvedUiText(context, key, value) + suffix) as T;
  }
  if (Array.isArray(value)) {
    const rendered = value.map((_item, index) => approvedUiSourceField(context, value, index));
    return (rendered.some((item, index) => item !== value[index]) ? rendered : value) as T;
  }
  // JSX is localized where it is authored. Container props never re-resolve translated descendants.

  return value;
}

export interface ApprovedUiDataField {
  path: readonly (string | number)[];
  key: string;
  prefix?: string;
  suffix?: string;
}
interface ApprovedUiArgument {
  original: unknown;
  text: string;
}
const approvedUiArguments = new WeakSet<object>();
const isApprovedUiArgument = (value: unknown): value is ApprovedUiArgument => Boolean(value && typeof value === 'object' && approvedUiArguments.has(value as object));
function approvedUiArgument(original: unknown, translated: unknown): ApprovedUiArgument {
  const value = { original, text: `${translated}` };
  approvedUiArguments.add(value);
  return value;
}
export function approvedUiFieldArgument<T, K extends keyof T>(context: ApprovedUiValue | null, owner: T, property: K): unknown {
  const original = owner[property];
  return approvedUiArgument(original, approvedUiBoundSourceField(context, original, owner, property));
}
export function approvedUiArrayArgument(context: ApprovedUiValue | null, values: readonly unknown[], separator = ','): unknown {
  return approvedUiArgument(values.join(separator), values.map((_value, index) => approvedUiSourceField(context, values, index)).join(separator));
}
export interface ApprovedUiDataOrigin {
  path: readonly (string | number)[];
  owner: unknown;
  property: PropertyKey;
}
export interface ApprovedUiDataSpread {
  owner: unknown;
  excluded: readonly PropertyKey[];
}
const approvedUiDataFields = new WeakMap<object, Map<PropertyKey, ApprovedUiDataField>>();

/** Register immutable original field identities; no hook, value replacement or dataset mutation. */
export function registerApprovedUiData<T>(data: T, fields: readonly ApprovedUiDataField[], origins: readonly ApprovedUiDataOrigin[] = [], spreads: readonly ApprovedUiDataSpread[] = []): T {
  for (const [index, spread] of spreads.entries()) {
    if (!spread.owner || typeof spread.owner !== 'object') continue;
    // Object spread copies own enumerable fields in order: a later source owns an overridden field.
    // Keep explicit registration conflicts strict; only the winning spread contributes its source key.
    const laterSpreads = spreads.slice(index + 1);
    for (const [property, inherited] of approvedUiDataFields.get(spread.owner) ?? []) {
      const copied = Object.prototype.propertyIsEnumerable.call(spread.owner, property);
      const overridden = laterSpreads.some((later) => later.owner != null && Object.prototype.propertyIsEnumerable.call(later.owner, property));
      if (copied && !overridden && !spread.excluded.includes(property)) registerApprovedUiData(data, [{ ...inherited, path: [property as string | number] }]);
    }
  }
  for (const field of fields) {
    let owner: unknown = data;
    for (const property of field.path.slice(0, -1)) owner = owner && typeof owner === 'object' ? (owner as Record<PropertyKey, unknown>)[property] : undefined;
    if (!owner || typeof owner !== 'object' || !field.path.length) continue;
    const property = field.path[field.path.length - 1];
    if (property === undefined) continue;
    const known = approvedUiDataFields.get(owner) ?? new Map<PropertyKey, ApprovedUiDataField>();
    const prior = known.get(property);
    if (prior && prior.key !== field.key) throw new Error(`Ambiguous frozen UI field registration: ${String(property)}`);
    known.set(property, field);
    approvedUiDataFields.set(owner, known);
  }
  for (const origin of origins) {
    if (!origin.owner || typeof origin.owner !== 'object') continue;
    const inherited = approvedUiDataFields.get(origin.owner)?.get(origin.property);
    if (inherited) registerApprovedUiData(data, [{ ...inherited, path: origin.path }]);
  }
  return data;
}

/** Only the original registered field is translated. Native/render-created objects are untouched. */
export function approvedUiSourceField<T, K extends keyof T>(context: ApprovedUiValue | null, owner: T, property: K): T[K] {
  const value = owner[property];
  return approvedUiBoundSourceField(context, value, owner, property);
}

export function approvedUiBoundSourceField<T>(context: ApprovedUiValue | null, value: T, owner: unknown, property: PropertyKey): T {
  if (!context || !owner || typeof owner !== 'object' || typeof value !== 'string') return value;
  const field = approvedUiDataFields.get(owner)?.get(property);
  if (!field) return value;
  const template = context.sourceTemplates?.find((entry) => entry.key === field.key && entry.original === value);
  return (template ? template.text : (field.prefix ?? '') + approvedUiText(context, field.key, value) + (field.suffix ?? '')) as T;
}

/** Key registered (registerApprovedUiData) for `owner[property]`, or undefined: for resolvers that must not throw (code-literals.ts). */
export function approvedUiFieldKey(owner: unknown, property: PropertyKey): string | undefined {
  return owner && typeof owner === 'object' ? approvedUiDataFields.get(owner)?.get(property)?.key : undefined;
}

export function approvedUiOptionalSourceField<T, K extends keyof NonNullable<T>>(context: ApprovedUiValue | null, owner: T, property: K): NonNullable<T>[K] | undefined {
  return owner == null ? undefined : approvedUiSourceField(context, owner as NonNullable<T>, property);
}

/** Preserve raw projections and attach their field identities until a public string-array sink renders them. */
export function projectApprovedUiFields<T, K extends keyof T>(data: readonly T[], property: K): T[K][] {
  const result = data.map((item) => item[property]);
  const fields = data.flatMap((item, index) => {
    const field = item && typeof item === 'object' ? approvedUiDataFields.get(item)?.get(property) : undefined;
    return field ? [{ ...field, path: [index] }] : [];
  });
  return registerApprovedUiData(result, fields);
}
